<script setup lang="ts">
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {usePointerDragDrop} from '@toolify/client/src/modules/ui/composables/usePointerDragDrop'
import {computed} from 'vue'

const context = usePointerDragDrop()
const isDragging = computed(() => context?.isDragging.value === true)
const preview = computed(() => context?.preview.value ?? null)
const previewStyle = computed(() => {
  if(!context) {
    return {}
  }
  return {
    transform: `translate(${context.pointerX.value + 12}px, ${context.pointerY.value + 12}px)`,
  }
})
const insertLineStyle = computed(() => context?.insertLineStyle.value ?? null)
const previewItems = computed(() => {
  if(!preview.value) {
    return []
  }
  if(preview.value.items?.length) {
    return preview.value.items
  }
  return [{
    iconNameType: preview.value.iconNameType,
    name: preview.value.name,
    iconColor: preview.value.iconColor,
  }]
})
const extraCount = computed(() => preview.value?.extraCount ?? 0)
</script>

<template>
  <div
    v-if="isDragging && preview"
    class="pointer-drag-preview"
    :class="{'is-stack': previewItems.length > 1 || extraCount > 0}"
    :style="previewStyle"
  >
    <div
      v-for="(item, index) in previewItems"
      :key="`${item.name}-${index}`"
      class="preview-row"
    >
      <IconComponent
        :name="item.iconNameType"
        :weightType="IconWeightType.Solid"
        :color="null"
        :style="item.iconColor ? {color: item.iconColor} : undefined"
      />
      <span class="name">{{item.name}}</span>
    </div>
    <div v-if="extraCount > 0" class="extra">+ {{extraCount}}</div>
  </div>
  <div
    v-if="isDragging && insertLineStyle"
    class="pointer-drag-insert-line"
    :style="insertLineStyle"
  />
</template>

<style scoped lang="scss">
@use '@toolify/client/src/styles/variables' as variables;

.pointer-drag-preview {
  position: fixed;
  top: 0;
  left: 0;
  pointer-events: none;
  display: grid;
  gap: map-get(variables.$spacing, space-1);
  padding: 0.375rem 0.625rem;
  background: map-get(variables.$colors, container);
  color: map-get(variables.$colors, content-normal);
  border-radius: variables.$borderRadius;
  box-shadow: map-get(variables.$shadows, shadow-md);
  @include variables.zIndex(dialog);
  max-width: 16rem;
}

.preview-row {
  display: flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-2);
  min-width: 0;
}

.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.extra {
  color: map-get(variables.$colors, content-dim);
  font-weight: 600;
}

.pointer-drag-insert-line {
  position: fixed;
  pointer-events: none;
  background: map-get(variables.$colors, primary);
  @include variables.zIndex(dialog);
}
</style>

<style lang="scss">
html.is-pointer-dragging [data-drop-item-id] {
  pointer-events: none;
}
</style>
